<template>
  <div
    class="atom-input atom-input-checkbox__wrapper"
    :class="[
      { 'is-disabled': isDisabled },
      { 'has-error': showErrors },
      { 'is-disabled-emits': disableEmits },
      `is-size-${textSize}`,
    ]"
    @focusin="setHover(true)"
    @focus="setFocus(true)"
    @focusout="setFocus(false)"
  >
    <div
      class="atom-input-checkbox__box"
      :class="{ 'is-active': inputValue }"
    />

    <input
      :id="name"
      v-model="inputValue"
      type="checkbox"
      class="atom-input-checkbox__input"
    />

    <label
      class="atom-input-checkbox__label"
      :for="name"
    >
      <AtomTextRichtext
        :html="label"
        class="atom-input-checkbox__label"
        text-color="color"
      />
    </label>

    <transition name="fade-in-slow">
      <AtomInputErrors
        v-if="showErrors"
        :errors="errors"
        :is-extended="showErrorList"
        :is-checkbox="true"
      />
    </transition>
  </div>
</template>

<script setup>
const props = defineProps({
    label: {
        type: String,
        required: true,
        validator: (value) => value.length > 0,
    },

    name: {
        type: String,
        required: true,
        validator: (value) => value.length > 0 && !value.includes(' '),
    },

    initialValue: {
        type: Boolean,
        default: false,
        validator: (value) => typeof value === 'boolean',
    },

    disableEmits: {
        type: Boolean,
        default: false,
        validator: (value) => typeof value === 'boolean',
    },

    /*
          Validations
      */
    validations: {
        type: Array,
        default: () => [],
        validator: (value) => Array.isArray(value),
    },

    /*
          Variants
    */
    isDisabled: {
        type: Boolean,
        default: false,
        validator: (value) => typeof value === 'boolean',
    },

    /*
          Styles
    */
    textColor: {
        type: String,
        default: 'var(--c-black)',
    },

    checkboxColor: {
        type: String,
        default: 'var(--c-primary)',
    },

    textSize: {
        type: String,
        default: 'default',
    },
});

const {
    textColor,
    checkboxColor,
} = toRefs(props);

/*
      Intialze formfield
*/
const errors = ref([]);
const inputValue = ref(props.initialValue) || '';

/*
      Handle input
*/
const emit = defineEmits(['set-input', 'set-error', 'on-reset']);

/* Watch input */
const isResetting = ref(false);
const handleData = (value) => {
    if (props.disableEmits || props.isDisabled) return;

    if (isResetting.value) {
        errors.value = [];
        isResetting.value = false;
        return;
    }

    const { validationErrors } = useFormfieldEmit(
        value,
        props.name,
        emit,
        props.validations,
    );

    errors.value = validationErrors;
};
watch(() => inputValue.value, (newValue) => {
    handleData(newValue);
});

handleData(inputValue.value);

/*
      States
*/
const {
    setHover,
    setFocus,
    showErrors,
    showErrorList,
} = useFormFieldStates(inputValue, errors, props);

const resetValue = () => {
    isResetting.value = true;
    inputValue.value = props.initialValue || '';
    emit('on-reset');
};

defineExpose({
    resetValue,
    inputValue,
    name: props.name,
});
</script>

<style lang="scss" scoped>
.atom-input-checkbox__wrapper {
    @include formFieldWrapper;
    @include fluid('--checkbox-size', 18px, 18px, 18px);
    @include fluid('column-gap', 15px, 15px, 15px, false);

    --text-color: v-bind(textColor);
    --checkbox-color: v-bind(checkboxColor);
    display: flex;
    align-items: center;
    padding-top: 7px;
    padding-right: 40px;
    color: var(--text-color);
    font-family: var(--f-family--primary);
    font-size: var(--f-size--form-label);
    font-weight: var(--f-weight--500);
    line-height: var(--l-height--form-label);

    .block-registration-newsletter & {
        margin-top: 10px;
    }

    &.is-disabled {
        cursor: default;
        opacity: 0.2;
        pointer-events: none;
    }

    &.is-disabled-emits {
        & > * {
            pointer-events: none;
        }
    }
}

.atom-input-checkbox__input {
    position: absolute;
    left: 0;
    width: var(--checkbox-size);
    min-width: var(--checkbox-size);
    height: var(--checkbox-size);
    cursor: pointer;
    opacity: 0;

    .is-disabled & {
        cursor: default;
    }
}

.atom-input-checkbox__box {
    position: relative;
    width: var(--checkbox-size);
    min-width: var(--checkbox-size);
    height: var(--checkbox-size);
    border: 2px solid var(--checkbox-color);
    border-radius: 3px;
    pointer-events: none;

    &:after {
        @include fluid('height', 6px, 6px, 6px, false);
        @include fluid('width', 6px, 6px, 6px, false);

        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        border-radius: 1px;
        margin: auto;
        background: var(--checkbox-color);
        content: '';
        opacity: 0;
        transform: scale(0.25);
        transition: opacity 0.2s var(--ease-out--quart), transform 0.2s var(--ease-out--quart);
    }

    &.is-active {
        &:after {
            opacity: 1;
            transform: scale(1);
        }
    }
}

.atom-input-checkbox__label {
    cursor: pointer;
    user-select: none;

    ::v-deep(.atom-richtext__inner) {
        font-size: var(--f-size--form-label);
        font-weight: var(--f-weight--500);
        line-height: var(--l-height--form-label);
    }

    .is-size-big & {
        ::v-deep(.atom-richtext__inner) {
            font-size: var(--f-size--form-label);
            font-weight: var(--f-weight--500);
            line-height: var(--l-height--form-label);
        }
    }

    .is-disabled & {
        cursor: default;
    }
}
</style>
